import { fieldModerationSchema, fieldRubricDimensionSchema } from '@mio/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';
import { AppShell } from '@/shared/layout/app-shell';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';
import { LoadingStatus } from '@/shared/ui/spinner';

import {
  beginAppealReview,
  moderateField,
  resolveFieldAppeal,
  useFieldAdminDetail,
  useFieldAdminList,
} from '../api/field-admin-client';
import { FieldAdminEvidence } from '../components/field-admin-evidence';
import { FieldStatus } from '../components/field-primitives';
import { FIELD_INPUT, FIELD_LABEL } from '../components/field-types';
import { fieldErrorMessage } from '../field-view';

import type { FieldAdminDetail, FieldModeration, FieldAppeal } from '@mio/shared';
function humanLabel(value: string) {
  return (value.charAt(0).toUpperCase() + value.slice(1).replaceAll('_', ' ')).replace(
    /^Ai /,
    'AI ',
  );
}
const emptyRatings = () =>
  fieldRubricDimensionSchema.options.map((dimension) => ({
    dimension,
    rating: 0,
    rationale: '',
    evidence: '',
  }));

function ModerationForm({ detail, onSaved }: { detail: FieldAdminDetail; onSaved: () => void }) {
  const [prId, setPrId] = useState('');
  const [candidateRevision, setCandidateRevision] = useState('');
  const [day, setDay] = useState<5 | 10>(detail.snapshot.engagement.day < 10 ? 5 : 10);
  const [ratings, setRatings] = useState(emptyRatings);
  const [assistance, setAssistance] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const publishing = useRef(false);
  const pending = useRef<{ fingerprint: string; body: FieldModeration } | null>(null);
  const selected = detail.snapshot.prs.find((p) => p.id === prId);
  const candidateChanged = Boolean(selected && selected.revision !== candidateRevision);
  const publish = async (body: FieldModeration) => {
    if (publishing.current) return;
    publishing.current = true;
    setBusy(true);
    setError(null);
    try {
      await moderateField(detail.learnerId, body);
      pending.current = null;
      setUncertain(false);
      setPrId('');
      setCandidateRevision('');
      setRatings(emptyRatings());
      setAssistance('');
      setFeedback('');
      onSaved();
    } catch (cause) {
      setError(fieldErrorMessage(cause));
      if (cause instanceof ApiRequestError && cause.status < 500) {
        pending.current = null;
        setUncertain(false);
        if (cause.status === 409) onSaved();
      } else {
        // The server may have committed the report before the response was lost.
        // Preserve its exact request until a replay establishes the outcome.
        setUncertain(true);
      }
    } finally {
      publishing.current = false;
      setBusy(false);
    }
  };
  return (
    <form
      className="min-w-0 space-y-4 [overflow-wrap:anywhere]"
      onSubmit={(event) => {
        event.preventDefault();
        if (!selected || candidateChanged || publishing.current || uncertain) return;
        setError(null);
        try {
          const value = {
            checkpointDay: day,
            prId,
            revision: candidateRevision,
            ratings: ratings.map(({ evidence, ...rating }) => ({
              ...rating,
              evidenceIds: evidence
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean),
            })),
            assistance,
            feedback,
          };
          const fingerprint = JSON.stringify(value);
          const body =
            pending.current?.fingerprint === fingerprint
              ? pending.current.body
              : fieldModerationSchema.parse({
                  ...value,
                  expectedRevision: detail.snapshot.engagement.revision,
                  requestId: crypto.randomUUID(),
                });
          pending.current = { fingerprint, body };
          void publish(body);
        } catch (cause) {
          setError(fieldErrorMessage(cause));
        }
      }}
    >
      <fieldset disabled={busy || uncertain} className="min-w-0 space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className={FIELD_LABEL} htmlFor="review-day">
              Checkpoint
            </label>
            <SelectField
              id="review-day"
              value={day}
              onValueChange={(nextValue) => setDay(nextValue === '5' ? 5 : 10)}
              options={[
                {
                  value: '5',
                  label: detail.snapshot.discovery
                    ? 'Progress review · formative'
                    : 'Day 5 · formative trajectory',
                },
                {
                  value: '10',
                  label: detail.snapshot.discovery
                    ? 'Readiness review · final'
                    : 'Day 10 · final readiness',
                },
              ]}
            />
          </div>
          <div>
            <label className={FIELD_LABEL} htmlFor="review-pr">
              Frozen candidate
            </label>
            <SelectField
              id="review-pr"
              value={prId}
              onValueChange={(nextValue) => {
                setPrId(nextValue);
                setCandidateRevision(
                  detail.snapshot.prs.find((p) => p.id === nextValue)?.revision ?? '',
                );
              }}
              options={[
                { value: '', label: 'Select candidate' },
                ...(detail.snapshot.prs.map((p) => ({
                  value: p.id,
                  label: (
                    <>
                      {p.title} · {p.revision.slice(0, 12)}
                    </>
                  ),
                })) ?? []),
              ]}
            />
          </div>
        </div>
        {candidateChanged ? (
          <Alert>
            <p>
              This candidate changed while you were reviewing it. Your draft is preserved for
              revision {candidateRevision.slice(0, 12)}. Inspect the updated evidence before using
              the current revision.
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() => {
                if (selected) setCandidateRevision(selected.revision);
                pending.current = null;
                setError(null);
              }}
            >
              Use current revision
            </Button>
          </Alert>
        ) : null}
        <div className="text-fg-muted space-y-3 text-sm leading-6">
          <p>
            Rate observed evidence only. Formative feedback does not complete the engagement. Final
            readiness also requires mandatory gates and minimum scores in required dimensions.
          </p>
          <details>
            <summary className="text-fg cursor-pointer font-medium">
              Rating scale: what each score means
            </summary>
            <ol start={0} className="mt-2 list-decimal space-y-1 pl-5">
              <li>
                Materially incorrect work, or a required result missing after a valid opportunity.
              </li>
              <li>Partial work with important unresolved errors or unsupported claims.</li>
              <li>Adequate routine behavior with meaningful gaps or missing failure handling.</li>
              <li>Correct, justified, reproducible work across normal and failure conditions.</li>
              <li>
                Strong performance under meaningful variation, with clear limitations and tradeoffs.
              </li>
            </ol>
            <p className="mt-2">
              A missing opportunity is insufficient evidence. Gather the required observations
              before publishing a rating.
            </p>
          </details>
        </div>
        {ratings.map((r, index) => (
          <fieldset
            key={r.dimension}
            className="border-line grid min-w-0 gap-4 border-t py-4 md:grid-cols-[9rem_minmax(0,1fr)]"
          >
            <legend className="px-1 text-sm font-medium">{humanLabel(r.dimension)}</legend>
            <label className={FIELD_LABEL}>
              Rating
              <SelectField
                value={r.rating}
                onValueChange={(nextValue) =>
                  setRatings((current) =>
                    current.map((v, i) => (i === index ? { ...v, rating: Number(nextValue) } : v)),
                  )
                }
                options={[
                  ...([0, 1, 2, 3, 4].map((n) => ({ value: n, label: <>{n} / 4</> })) ?? []),
                ]}
              />
            </label>
            <div className="space-y-2">
              <label className={FIELD_LABEL}>
                Evidence-based rationale
                <textarea
                  className={FIELD_INPUT}
                  required
                  minLength={20}
                  maxLength={4000}
                  rows={2}
                  value={r.rationale}
                  onChange={(e) =>
                    setRatings((current) =>
                      current.map((v, i) =>
                        i === index ? { ...v, rationale: e.target.value } : v,
                      ),
                    )
                  }
                />
              </label>
              <label className={FIELD_LABEL}>
                Evidence IDs
                <input
                  className={FIELD_INPUT}
                  required
                  value={r.evidence}
                  onChange={(e) =>
                    setRatings((current) =>
                      current.map((v, i) => (i === index ? { ...v, evidence: e.target.value } : v)),
                    )
                  }
                />
              </label>
            </div>
          </fieldset>
        ))}
        <label className={FIELD_LABEL}>
          Assistance context
          <textarea
            className={FIELD_INPUT}
            rows={2}
            maxLength={8000}
            value={assistance}
            onChange={(e) => setAssistance(e.target.value)}
          />
        </label>
        <label className={FIELD_LABEL}>
          Feedback and next steps
          <textarea
            className={FIELD_INPUT}
            required
            minLength={20}
            maxLength={16000}
            rows={4}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </label>
        {error ? <Alert>{error}</Alert> : null}
        <Button type="submit" loading={busy} disabled={!selected || candidateChanged}>
          Publish moderated report
        </Button>
      </fieldset>
      {uncertain ? (
        <Alert>
          <p>
            The publication response was lost. Your draft is locked until its outcome is known.
            Retry the same publication to recover its result without creating another report.
          </p>
          <Button
            type="button"
            size="sm"
            className="mt-2"
            loading={busy}
            onClick={() => {
              if (pending.current) void publish(pending.current.body);
            }}
          >
            Retry publication
          </Button>
        </Alert>
      ) : null}
    </form>
  );
}
function AppealReview({
  appeal,
  detail,
  onSaved,
}: {
  appeal: FieldAppeal;
  detail: FieldAdminDetail;
  onSaved: () => void;
}) {
  const [response, setResponse] = useState('');
  const [replacement, setReplacement] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(crypto.randomUUID());
  return (
    <article className="border-line min-w-0 space-y-3 border-t py-4 [overflow-wrap:anywhere]">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold">Appeal</h3>
        <FieldStatus value={appeal.status} />
      </div>
      <p className="whitespace-pre-wrap text-sm">{appeal.reason}</p>
      <p className="text-fg-muted break-all text-xs">Evidence: {appeal.evidenceIds.join(', ')}</p>
      {appeal.status !== 'resolved' ? (
        <Button
          size="sm"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void beginAppealReview(detail.learnerId, appeal.id, {
              requestId: request.current,
              expectedRevision: detail.snapshot.engagement.revision,
            })
              .then(onSaved)
              .catch((cause) => setError(fieldErrorMessage(cause)))
              .finally(() => setBusy(false));
          }}
        >
          {appeal.status === 'submitted' ? 'Open reassessment' : 'Resume reassessment'}
        </Button>
      ) : null}
      {appeal.status === 'under_review' ? (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void resolveFieldAppeal(detail.learnerId, appeal.id, {
              requestId: request.current,
              response,
              replacementReportDigest: replacement,
            })
              .then(onSaved)
              .catch((cause) => setError(fieldErrorMessage(cause)))
              .finally(() => setBusy(false));
          }}
        >
          <label className={FIELD_LABEL}>
            New moderated report
            <SelectField
              value={replacement}
              onValueChange={(nextValue) => setReplacement(nextValue)}
              options={[
                { value: '', label: 'Publish reassessment first' },
                ...(detail.reports
                  .filter(
                    (r) =>
                      r.digest !== appeal.reportDigest &&
                      r.report.candidateRevision === appeal.candidateRevision,
                  )
                  .map((r) => ({
                    value: r.digest,
                    label: (
                      <>
                        {new Date(r.report.moderatedAt).toLocaleString()} · {r.report.weightedScore}
                        /100
                      </>
                    ),
                  })) ?? []),
              ]}
            />
          </label>
          <label className={FIELD_LABEL}>
            Response to learner
            <textarea
              className={FIELD_INPUT}
              required
              minLength={40}
              maxLength={8000}
              value={response}
              onChange={(e) => setResponse(e.target.value)}
            />
          </label>
          <Button type="submit" loading={busy} disabled={!replacement}>
            Resolve with preserved report history
          </Button>
        </form>
      ) : (
        <p className="text-sm">{appeal.response}</p>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </article>
  );
}
export function FieldAdminPage() {
  const [cursor, setCursor] = useState<string | undefined>();
  const list = useFieldAdminList(cursor);
  const [learnerId, setLearnerId] = useState<string | null>(null);
  const [evidenceQuery, setEvidenceQuery] = useState('');
  const detail = useFieldAdminDetail(learnerId);
  const evidenceSnapshot = detail.data?.snapshot;
  const evidenceItems = evidenceSnapshot
    ? [
        ...(evidenceSnapshot.discovery?.decisions.map((v) => ({
          id: v.id,
          label: `Decision · ${v.title} · version ${v.version} · ${v.status}`,
          text: `${v.description}\nOwner: ${v.owner}\nEvidence revisions: ${JSON.stringify(v.sourceRevisions)}\nHistory: ${v.history.map((entry) => `v${entry.version} ${entry.status} by ${entry.by} at ${entry.at}: ${entry.description}; ${entry.rationale}`).join('\n')}`,
        })) ?? []),
        ...(evidenceSnapshot.discovery?.findings.map((v) => ({
          id: v.id,
          label: `Finding · ${v.title} · ${humanLabel(v.category)} · ${v.status}`,
          text: `${v.description}\nOwner: ${v.owner}\nEvidence: ${v.evidenceIds.join(', ')}\nDecision versions: ${v.decisionRefs.map((ref) => `${ref.decisionId} v${ref.version}`).join(', ')}\nLearner response: ${v.response ?? 'None'}\nResolution: ${v.resolution?.rationale ?? 'Pending'}`,
        })) ?? []),
        ...evidenceSnapshot.prs.map((v) => ({
          id: v.id,
          label: `Pull request · ${v.title} · ${v.revision.slice(0, 8)}`,
          text: v.description,
        })),
        ...evidenceSnapshot.documents.map((v) => ({
          id: v.id,
          label: `Document v${v.version}: ${v.title}`,
          text: `${v.status}; source references ${v.sourceIds.join(', ')}`,
        })),
        ...evidenceSnapshot.runs.map((v) => ({
          id: v.id,
          label: `${humanLabel(v.suite)} run · ${humanLabel(v.status)} · ${v.trusted ? 'Trusted' : 'Untrusted'}`,
          text: v.summary,
        })),
        ...evidenceSnapshot.messages.map((v) => ({
          id: v.id,
          label: `${evidenceSnapshot.actors.find((actor) => actor.id === v.actorId)?.name ?? v.actorId} · ${v.role === 'learner' ? 'Learner message' : v.role === 'system' ? 'Project update' : 'Colleague message'}`,
          text: v.text,
        })),
        ...evidenceSnapshot.incidents.map((v) => ({
          id: v.id,
          label: v.title,
          text: humanLabel(v.status),
        })),
        ...evidenceSnapshot.tickets.map((v) => ({
          id: v.id,
          label: v.title,
          text: humanLabel(v.status),
        })),
      ].filter((item) =>
        `${item.label} ${item.id} ${item.text}`
          .toLowerCase()
          .includes(evidenceQuery.trim().toLowerCase()),
      )
    : [];
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['field-admin-detail'] });
    void client.invalidateQueries({ queryKey: ['field-admin-list'] });
  };
  return (
    <AppShell title="Workbench instructor review">
      <div className="mx-auto min-w-0 max-w-6xl space-y-5 px-4 py-6 [overflow-wrap:anywhere] sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="page-title">Instructor review</h1>
          <Link to="/field" className="text-link text-sm">
            Back to Workbench
          </Link>
        </div>
        <p className="text-fg-muted text-sm">
          Review the current evidence, publish formative or final feedback, and resolve learner
          appeals. Access is limited to configured Workbench instructors.
        </p>
        {list.error ? (
          <Alert>
            <p>{fieldErrorMessage(list.error)}</p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              loading={list.isFetching}
              onClick={() => void list.refetch()}
            >
              Retry engagements
            </Button>
          </Alert>
        ) : null}
        {list.isPending ? <LoadingStatus label="Loading Workbench engagements…" /> : null}
        {list.data?.engagements.length === 0 ? (
          <p className="text-fg-muted text-sm">No engagements are available for review yet.</p>
        ) : null}
        <div
          className="border-line max-w-full overflow-x-auto border-y"
          tabIndex={0}
          role="region"
          aria-label="Workbench engagements"
        >
          <table className="w-full text-left text-sm">
            <caption className="sr-only">
              Select an engagement to review its learner evidence.
            </caption>
            <thead>
              <tr>
                <th scope="col" className="p-3">
                  Engagement / learner
                </th>
                <th scope="col" className="px-3 py-3">
                  Day
                </th>
                <th scope="col" className="px-3 py-3">
                  Assessment
                </th>
                <th scope="col" className="px-3 py-3">
                  Score
                </th>
                <th scope="col" className="px-3 py-3">
                  Appeals
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data?.engagements.map((row) => (
                <tr key={row.engagementId} className="border-line border-t">
                  <td className="p-3">
                    <button
                      type="button"
                      className="text-link text-left text-sm font-semibold"
                      aria-current={row.learnerId === learnerId ? 'true' : undefined}
                      onClick={() => {
                        setLearnerId(row.learnerId);
                        setEvidenceQuery('');
                      }}
                    >
                      {row.title}
                    </button>
                    <p className="text-fg-muted mt-1 text-xs" title={row.learnerId}>
                      Learner {row.learnerId.slice(0, 8)}
                    </p>
                  </td>
                  <td className="px-3 tabular-nums">{row.day}</td>
                  <td className="px-3">
                    <FieldStatus value={row.assessmentStatus} />
                  </td>
                  <td className="px-3 tabular-nums">
                    {row.latestScore === null ? 'Not graded' : `${row.latestScore}/100`}
                  </td>
                  <td className="px-3 tabular-nums">{row.pendingAppeals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data?.nextCursor ? (
          <Button variant="secondary" onClick={() => setCursor(list.data?.nextCursor ?? undefined)}>
            Next engagements
          </Button>
        ) : null}
        {detail.error ? (
          <Alert>
            <p>{fieldErrorMessage(detail.error)}</p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              loading={detail.isFetching}
              onClick={() => void detail.refetch()}
            >
              Retry learner evidence
            </Button>
          </Alert>
        ) : null}
        {learnerId && detail.isFetching ? (
          <LoadingStatus label="Loading learner evidence…" />
        ) : null}
        {detail.data ? (
          <div className="min-w-0 space-y-6">
            <div>
              <h2 className="text-lg font-semibold">{detail.data.snapshot.engagement.title}</h2>
              <p className="text-fg-muted mt-1 text-sm" title={detail.data.learnerId}>
                Learner {detail.data.learnerId.slice(0, 8)} · Day{' '}
                {detail.data.snapshot.engagement.day}
              </p>
            </div>
            <details className="border-line min-w-0 border-y py-4">
              <summary className="cursor-pointer text-sm">Evidence available for citation</summary>
              <label htmlFor="admin-evidence-search" className={`${FIELD_LABEL} mt-4`}>
                Find evidence
              </label>
              <input
                id="admin-evidence-search"
                type="search"
                className={FIELD_INPUT}
                placeholder="Search titles, IDs, and content"
                value={evidenceQuery}
                onChange={(event) => setEvidenceQuery(event.target.value)}
              />
              <div className="mt-4 max-h-[30rem] space-y-3 overflow-y-auto">
                {!evidenceItems.length ? (
                  <p className="text-fg-muted text-sm">
                    {evidenceQuery
                      ? 'No evidence matches this search.'
                      : 'No evidence has been recorded yet.'}
                  </p>
                ) : null}
                {evidenceItems.map((item) => (
                  <article key={item.id} className="border-line min-w-0 border-t py-3">
                    <p className="text-sm font-medium">{item.label}</p>
                    <code className="text-fg-muted break-all text-xs">{item.id}</code>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-6">{item.text}</p>
                  </article>
                ))}
              </div>
            </details>
            <section className="space-y-3">
              <h3 className="text-base font-semibold">Previous reports</h3>
              {!detail.data.reports.length ? (
                <p className="text-fg-muted text-sm">No moderated reports yet.</p>
              ) : null}
              {detail.data.reports.map((r) => (
                <p key={r.digest} className="text-sm">
                  {detail.data?.snapshot.discovery
                    ? r.report.checkpointDay === 5
                      ? 'Progress review'
                      : 'Readiness review'
                    : `Day ${r.report.checkpointDay ?? 10}`}{' '}
                  · {r.report.weightedScore}/100 · {r.report.feedback}
                </p>
              ))}
            </section>
            <FieldAdminEvidence key={detail.data.learnerId} detail={detail.data} />
            {detail.data.appeals.map((appeal) => (
              <AppealReview
                key={appeal.id}
                appeal={appeal}
                detail={detail.data}
                onSaved={refresh}
              />
            ))}
            {detail.data.advice ? (
              <section className="border-line min-w-0 space-y-3 border-t py-4 [overflow-wrap:anywhere]">
                <h3 className="text-sm font-medium">
                  Provisional model advice · instructor review required
                </h3>
                <p className="text-fg-muted break-all text-xs">
                  Revision{' '}
                  <code title={detail.data.advice.candidateRevision}>
                    {detail.data.advice.candidateRevision.slice(0, 8)}
                  </code>
                </p>
                <ul className="space-y-2">
                  {detail.data.advice.dimensions.map((d) => (
                    <li key={d.dimension} className="text-sm">
                      <strong>
                        {humanLabel(d.dimension)}:{' '}
                        {d.rating === null ? 'Insufficient evidence' : `${d.rating}/4`}
                      </strong>
                      <p>{d.rationale}</p>
                      <p className="text-fg-muted break-all text-xs">{d.evidenceIds.join(', ')}</p>
                    </li>
                  ))}
                </ul>
                {detail.data.advice.limitations.map((limitation, index) => (
                  <p key={index} className="text-fg-muted text-xs">
                    {limitation}
                  </p>
                ))}
              </section>
            ) : null}
            <h3 className="text-base font-semibold">Publish an instructor report</h3>
            <ModerationForm
              key={`${detail.data.learnerId}:${detail.data.snapshot.engagement.id}`}
              detail={detail.data}
              onSaved={refresh}
            />
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
